import * as GuidedTourActions from 'app/guided-tour/state/guided-tour.actions';

import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import {
  DocumentModelFactoryService,
  DocumentModelType,
  Exercise,
  ExerciseProgramDocumentUtils,
  PlanDocumentUtils,
  PlannedTier,
  PlannedTierRow,
  Program,
  ResourceType,
  Tier,
  tierRowParams,
} from 'fitforce-document-sync';
import { cloneDeep, debounce, flatMap, isNil, isNumber, isString, parseInt, toString, uniq } from 'lodash';
import { Observable, Subscription } from 'rxjs';

import { TitleCasePipe } from '@angular/common';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TrainingLoadNoticeDialogComponent } from 'app/document/components/training-load-notice-dialog/training-load-notice-dialog.component';
import { DocumentResourceService } from 'app/document/services/document-resource.service';
import { EventBusService } from 'app/document/services/event-bus.service';
import { GuidedTourSelectors } from 'app/guided-tour/state/guided-tour.selectors';
import { GuidedTourDataService } from 'app/services/guided-tour-data.service';
import { v4 as uuid } from 'uuid';
import { ExerciseCategory } from '../../../document-planning/document-planning.component';
import { DocumentTierRowNotesDialogComponent } from './document-tier-row-notes-dialog/document-tier-row-notes-dialog.component';

interface CategorizedExercises {
  category: ExerciseCategory;
  exercises: Exercise[];
}

interface CategoryGroup {
  groupLabel: string;
  categories: CategorizedExercises[];
}

interface ParameterOption {
  param: string;
  unit: string;
  type: string;
}

@Component({
  selector: 'document-tier-planning',
  templateUrl: './document-tier-planning.component.html',
  styleUrls: ['./document-tier-planning.component.scss'],
})
export class DocumentTierPlanningComponent implements AfterViewInit, OnChanges, OnInit, OnDestroy {
  private subs: Subscription[] = [];

  private debouncedUpdateTier = debounce(this.updateTier.bind(this), 500);
  private debouncedUpdateRow = debounce(this.updateRow.bind(this), 500);

  private _filteredCategorizedExercises: CategorizedExercises[];

  private _categoryGroups: CategoryGroup[];

  parameterOverrides: ParameterOption[] = [];
  supportsParameterValidation = false;

  private categorizedExercises: CategorizedExercises[];
  private _possibleParams: string[] | ParameterOption[];
  private parametersToExercise = {};

  tierModel: PlannedTier;

  circuitControls: {
    isCircuit: FormControl;
    numRounds: FormControl;
    restTime: FormControl;
  } = {
    isCircuit: new FormControl(),
    numRounds: new FormControl(),
    restTime: new FormControl(),
  };

  guidedTourToggledOn = false;
  guidedTourStatusSub: Subscription;

  @Select(GuidedTourSelectors.toggleStatus)guidedTourToggleStatus$: Observable<boolean>;

  @Input() readonly canEdit: boolean;
  @Input() readonly canMoveExercises: boolean;
  @Input() readonly tier: PlannedTier;
  @Input() readonly tierTemplate: Tier;
  @Input() readonly exercises: Exercise[];
  @Input() readonly exerciseCategories: ExerciseCategory[];
  @Input() readonly tierMetadata: { tierNumber: number; totalTiers: number };
  @Input() readonly planMetadata: any;

  @Output() addedTierToLibrary: EventEmitter<PlannedTier> = new EventEmitter<PlannedTier>();
  @Output() tierUpdated: EventEmitter<PlannedTier> = new EventEmitter<PlannedTier>();
  @Output() nextTier: EventEmitter<void> = new EventEmitter<void>();
  @Output() previousTier: EventEmitter<void> = new EventEmitter<void>();
  @Output() tierDeleted: EventEmitter<PlannedTier> = new EventEmitter<PlannedTier>();
  @Output() completed: EventEmitter<void> = new EventEmitter<void>();

  get filteredCategorizedExercises(): CategorizedExercises[] {
    return this._filteredCategorizedExercises;
  }

  set filteredCategorizedExercises(value: CategorizedExercises[]) {
    this._filteredCategorizedExercises = value;
  }

  get categoryGroups(): CategoryGroup[] {
    return this._categoryGroups;
  }

  set categoryGroups(value: CategoryGroup[]) {
    this._categoryGroups = value;
  }
  get possibleParams(): string[] | ParameterOption[] {
    if (!this._possibleParams) {
      if (this.supportsParameterValidation) {
        if (this.tierTemplate) {
          this._possibleParams = ExerciseProgramDocumentUtils.getPossibleParamsFromTier(this.tierTemplate, {
            exercises: this.exercises,
          } as Program);
        } else {
          this._possibleParams = tierRowParams.map(param => {
            return {
              param: param,
              unit: '',
              type: 'text',
            };
          });
        }
      } else {
        if (this.tierTemplate) {
          this._possibleParams = uniq(flatMap(this.tierTemplate.rows, row => Object.keys(row.parameter_overrides)));
        } else {
          this._possibleParams = tierRowParams;
        }
      }
    }

    return this._possibleParams;
  }
  set possibleParams(value: string[] | ParameterOption[]) {
    this._possibleParams = value;
  }

  constructor(
    private dialog: MatDialog,
    private resourceService: DocumentResourceService,
    private factory: DocumentModelFactoryService,
    private titlecasePipe: TitleCasePipe,
    private bus: EventBusService,
    private store: Store,
    private guidedTourDataService: GuidedTourDataService
  ) { }

  ngOnInit() {
    this.categorizedExercises = this.generateCategorizedExercises(this.exercises, this.exerciseCategories);

    this.subs.push(
      this.circuitControls.isCircuit.valueChanges.subscribe(value => {
        if (value === true) {
          this.tierModel.circuit = {
            num_rounds: this.circuitControls.numRounds.value || -1,
            rest_time_ms: this.circuitControls.restTime.value ? this.circuitControls.restTime.value * 1000 : -1,
          };

          // remove any sets parameters
          this.tierModel.rows.forEach(row => {
            row.parameter_selections['sets'] = this.supportsParameterValidation ? undefined : '';
          });

          if (this.canEdit) {
            this.circuitControls.numRounds.enable({ emitEvent: false });
            this.circuitControls.restTime.enable({ emitEvent: false });
          }
        } else {
          this.tierModel.circuit = null;
          this.circuitControls.numRounds.setValue(undefined, { emitEvent: false });
          this.circuitControls.restTime.setValue(undefined, { emitEvent: false });
          this.circuitControls.numRounds.disable({ emitEvent: false });
          this.circuitControls.restTime.disable({ emitEvent: false });
        }

        this.debounceUpdateTier();
      }),
      this.circuitControls.numRounds.valueChanges.subscribe(value => {
        this.tierModel.circuit.num_rounds = value;
        this.debounceUpdateTier();
      }),
      this.circuitControls.restTime.valueChanges.subscribe(value => {
        this.tierModel.circuit.rest_time_ms = value * 1000;
        this.debounceUpdateTier();
      }),
      this.bus.subscribe('dayValidated', value => {
        this.updateTier();
      }),
    );

  }
  ngAfterViewInit() {
    this.store.dispatch(new GuidedTourActions.GetGuidedTourToggleStatus());
    this.guidedTourStatusSub = this.guidedTourToggleStatus$.subscribe(status => {
      if (status !== undefined) {
        this.guidedTourToggledOn = status;
        if (this.guidedTourToggledOn) {
          setTimeout(() => this.guidedTourDataService.startGuidedTour('edit-workout'), 1000);
        }
      }
    });
  }

  ngOnChanges() {
    // if there is no tier selected or tier selection has changed
    if (!this.tierModel || this.tierModel.id !== this.tier.id) {
      // if a tier was selected and the user can edit the plan, update the tier
      if (this.tierModel && this.canEdit) {
        this.updateTier();
      }

      // update parameter validation support check
      this.supportsParameterValidation = this.checkForParameterValidationSupport();
      this.tierModel = cloneDeep<PlannedTier>(this.tier);
      this.possibleParams = null;

      if (this.tierTemplate && this.tierModel.rows.length < this.tierTemplate.rows.length) {
        this.addRowsFromTemplate();
      }

      if (this.tierModel.circuit) {
        this.circuitControls.isCircuit.setValue(true, { emitEvent: false });
        this.circuitControls.numRounds.setValue(this.tierModel.circuit.num_rounds > -1 ? this.tierModel.circuit.num_rounds : undefined, {
          emitEvent: false,
        });
        this.circuitControls.restTime.setValue(
          this.tierModel.circuit.rest_time_ms > -1 ? this.tierModel.circuit.rest_time_ms / 1000 : undefined,
          {
            emitEvent: false,
          },
        );

        if (this.canEdit) {
          this.circuitControls.numRounds.enable({ emitEvent: false });
          this.circuitControls.restTime.enable({ emitEvent: false });
        } else {
          this.circuitControls.isCircuit.disable({ emitEvent: false });
          this.circuitControls.numRounds.disable({ emitEvent: false });
          this.circuitControls.restTime.disable({ emitEvent: false });
        }
      } else {
        this.circuitControls.isCircuit.setValue(false, { emitEvent: false });
        this.circuitControls.numRounds.setValue(undefined, { emitEvent: false });
        this.circuitControls.restTime.setValue(undefined, { emitEvent: false });
        this.circuitControls.numRounds.disable({ emitEvent: false });
        this.circuitControls.restTime.disable({ emitEvent: false });
      }

      if (this.supportsParameterValidation) {
        this.tierModel.rows.forEach((row, index) => {
          const exercise = this.exercises.find(ex => ex.id === row.exercise_id);
          if (exercise) {
            for (const key in row.parameter_selections) {
              if (exercise.parameters[key] && exercise.parameters[key].type === 'int-array') {
                // attempt to parse value of selection
                const parsed = parseInt(row.parameter_selections[key]);
                if (row.parameter_selections[key] === -1) {
                  // update values of -1 to display as unselected
                  this.tierModel.rows[index].parameter_selections[key] = undefined;
                } else if (parsed && isNumber(parsed)) {
                  // if parse was successul, update the selection
                  this.tierModel.rows[index].parameter_selections[key] = parsed;
                }
              } else {
                if (this.tierModel.rows[index].parameter_selections[key] === -1) {
                  this.tierModel.rows[index].parameter_selections[key] = undefined;
                }
              }
            }
          }
        });
      }
    }

    if (this.tierModel.rows.length === 0) {
      this.addEmptyRow();
    }

    this.tierModel.rows = this.tierModel.rows.sort(PlanDocumentUtils.sortTiers);
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  orderParams(params) {
    return ExerciseProgramDocumentUtils.orderParams(params);
  }

  getPossibleParamsForExercise(row: PlannedTierRow) {
    if (!row.exercise_id || this.tierTemplate) {
      return this.possibleParams;
    }

    if (!this.parametersToExercise[row.exercise_id]) {
      const exercise = this.exercises.find(ex => ex.id === row.exercise_id);
      if (exercise) {
        if (Object.keys(exercise.parameters).length > 0) {
          this.parametersToExercise[row.exercise_id] = ExerciseProgramDocumentUtils.getPossibleParamsForExercise(row.exercise_id, {
            exercises: this.exercises,
          } as Program);
        } else {
          this.parametersToExercise[row.exercise_id] = this.possibleParams;
        }
      } else {
        return this.possibleParams;
      }
    }

    return this.parametersToExercise[row.exercise_id];
  }

  addRowsFromTemplate() {
    this.tierModel.rows = [];
    this.tierTemplate.rows.forEach(template => {
      const foundRow = this.tier.rows.find(row => row.order === template.order);
      if (foundRow) {
        this.tierModel.rows.push(foundRow);
      } else {
        const row = this.factory.build<PlannedTierRow>(DocumentModelType.PlannedTierRow);
        row.order = template.order;
        row.parameter_selections = {};
        this.possibleParams.forEach(param => {
          if (param) {
            if (this.supportsParameterValidation) {
              row.parameter_selections[param.param] = '';
            } else {
              row.parameter_selections[param] = '';
            }
          }
        });

        this.tierModel.rows.push(row);
      }
    });
  }

  addEmptyRow() {
    const row = this.factory.build<PlannedTierRow>(DocumentModelType.PlannedTierRow);
    // row.order = this.tier.rows.length + 1;
    row.order = this.tierModel.rows.length + 1;
    row.parameter_selections = {};
    this.possibleParams.forEach(param => {
      if (param) {
        if (this.supportsParameterValidation) {
          row.parameter_selections[param.param] = '';
        } else {
          row.parameter_selections[param] = '';
        }
      }
    });

    this.tierModel.rows.push(row);

    this.updateTier();
  }

  dropped(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.tierModel.rows, event.previousIndex, event.currentIndex);
    this.tierModel.rows.forEach((row, index) => {
      row.order = index + 1;
    });

    this.updateTier();
  }

  debounceUpdateTier() {
    this.debouncedUpdateTier();
  }

  complete() {
    this.completed.emit();
  }

  next() {
    this.updateTier();
    this.nextTier.emit();
  }

  prev() {
    this.updateTier();
    this.previousTier.emit();
  }

  updateTier() {
    const tier = cloneDeep(this.tierModel);
    const tierParams = ExerciseProgramDocumentUtils.getPossibleParamsFromTier(this.tierTemplate, { exercises: this.exercises } as Program);
    tier.rows = [];

    this.tierModel.rows.forEach(row => {
      const clonedRow = cloneDeep<PlannedTierRow>(row);

      if (row.exercise_id && row.exercise_name) {
        if (this.supportsParameterValidation) {
          const exercise = this.exercises.find(ex => ex.id === row.exercise_id);
          if (exercise) {
            for (const key in row.parameter_selections) {
              if (row.parameter_selections.hasOwnProperty(key) && exercise.parameters.hasOwnProperty(key)) {
                if (exercise.parameters[key].type === 'int-array') {
                  if (!row.parameter_selections[key]) {
                    // update value of selection to -1 for storage
                    clonedRow.parameter_selections[key] = -1;
                  } else {
                    // attempt to parse parameter selection
                    const parsed = parseInt(row.parameter_selections[key]);
                    if (parsed) {
                      // if parse was successful, update the selection
                      clonedRow.parameter_selections[key] = parseInt(row.parameter_selections[key]);
                    }
                  }
                } else if (exercise.parameters[key].type === 'str-array') {
                  clonedRow.parameter_selections[key] = toString(row.parameter_selections[key]);
                }
              } else if (row.parameter_selections.hasOwnProperty(key) && tierParams.some(param => param.param === key)) {
                const tierParam = tierParams.find(param => param.param === key);
                if (tierParam.type === 'number') {
                  if (!row.parameter_selections[key]) {
                    // update value of selection to -1 for storage
                    clonedRow.parameter_selections[key] = -1;
                  } else {
                    // attempt to parse parameter selection
                    const parsed = parseInt(row.parameter_selections[key]);
                    if (parsed) {
                      // if parse was successful, update the selection
                      clonedRow.parameter_selections[key] = parseInt(row.parameter_selections[key]);
                    }
                  }
                } else if (tierParam.type === 'text') {
                  clonedRow.parameter_selections[key] = toString(row.parameter_selections[key]);
                }
              } else {
                if (!row.parameter_selections[key]) {
                  clonedRow.parameter_selections[key] = -1;
                }
              }
            }
          }
        }

        tier.rows.push(cloneDeep(clonedRow));
      }
    });

    this.tierUpdated.emit(cloneDeep<PlannedTier>(tier));
  }

  debounceUpdateRow($event: any, index: number) {
    this.debouncedUpdateRow($event, index);
  }

  displayFn(exercise: any): any {
    return exercise && exercise.name ? exercise.name : exercise;
  }

  updateRow($event: any, index: number) {
    if (isString($event)) {
      const foundExercise = this.exercises.find(ex => ex.name === $event);
      if (!foundExercise) {
        return;
      } else {
        this.tierModel.rows[index].exercise_id = foundExercise.id;
        this.tierModel.rows[index].exercise_name = foundExercise.name;
      }
    } else if ($event) {
      const exercise = $event as Exercise;
      this.tierModel.rows[index].exercise_id = exercise.id;
      this.tierModel.rows[index].exercise_name = exercise.name;
    } else {
      this.tierModel.rows[index].exercise_name = null;
      this.tierModel.rows[index].exercise_id = null;
    }

    this.updateTier();
  }

  validateExercise($event: any, index: number) {
    if ($event.target && $event.target.value && isString($event.target.value)) {
      const foundExercise = this.exercises.find(ex => ex.name === $event.target.value);
      if (!foundExercise) {
        this.tierModel.rows[index].exercise_id = null;
        this.tierModel.rows[index].exercise_name = null;

        this.updateTier();
      }
    }
  }

  validateParameter($event: any, rowIndex: number, paramIndex: number) {
    if (this.tierTemplate && $event.target && $event.target.value && isString($event.target.value)) {
      const foundParameter = this.parameterOverrides.find(param => param === $event.target.value);
      if (!foundParameter) {
        this.tierModel.rows[rowIndex].parameter_selections[paramIndex] = null;

        this.updateTier();
      }
    }
  }

  /**
   * Filters categorizedExercises
   * @param {string} val - value to filter
   * @param {number} index - index of row
   */
  filterCategorizedExercises(val: string, index: number) {
    // make sure val is not null and if its not a string, get its 'name' property
    if (isNil(val)) {
      val = '';
    } else if (!isString(val)) {
      val = val['name'];
    }

    let filtered: CategorizedExercises[];

    // Keep track of empty categories
    let emptyCategoryIndexes: number[] = [];

    if (this.tierTemplate) {
      // If working in a tier template
      // Look through exercises and find the ones needed
      filtered = [];
      const filteredExercises = this.exercises.filter(exercise => {
        return this.tierTemplate.rows[index]
          ? this.tierTemplate.rows[index].exercises.indexOf(exercise.id) > -1 && exercise.name.toLowerCase().indexOf(val.toLowerCase()) > -1
          : this.exercises.map(ex => ex.id).indexOf(exercise.id) > -1 && exercise.name.toLowerCase().indexOf(val.toLowerCase()) > -1;
      });

      // add these template exercises to
      filtered.unshift({
        category: { name: 'From Template', id: uuid() },
        exercises: filteredExercises,
      });

      // these indexs need to be updated because an item has been added
      emptyCategoryIndexes = emptyCategoryIndexes.map(i => i + 1);

      // if its empty add it to emptyCategoryIndexes
      if (filteredExercises.length === 0) {
        emptyCategoryIndexes.push(0);
      }
    } else {
      // If not working in a tier template, copy full list of categorized exercise
      filtered = cloneDeep(this.categorizedExercises);

      // for each CategorizedExercises...
      filtered.forEach((cat, i) => {
        // set the exercises to filter
        cat.exercises =
          cat.category.name.toLowerCase().indexOf(val.toLowerCase()) > -1
            ? cat.exercises
            : cat.exercises.filter(exercise => {
                return exercise.name.toLowerCase().indexOf(val.toLowerCase()) > -1;
              });

        // if category has no exercises, mark as empty.
        if (cat.exercises.length === 0) {
          emptyCategoryIndexes.push(i);
        }
      });
    }

    // remove all category with no exercises
    emptyCategoryIndexes.forEach((indexToRemove, i) => {
      // subtract current index, its the offset given objects removed
      filtered.splice(indexToRemove - i, 1);
    });

    this.filteredCategorizedExercises = filtered;
  }

  /**
   * Generates CategorizedExercises used to render drop down.
   * @param {Exercise[]} override - Array of Exercises being used to generate
   * @returns {CategorizedExercises[]}
   * @note If e is the number of total exercises and c is the number of total categories,
   * this problem is an order of complexity O(ce). e is a very large number, multiplying it
   * by c makes too large of a number for often traversal.
   */
  generateCategorizedExercises(exercises: Exercise[], exerciseCategories: ExerciseCategory[]): CategorizedExercises[] {
    const categorizedExercises = [];
    if (exercises === undefined || exerciseCategories === undefined) {
      return categorizedExercises;
    }
    // for each category...
    exerciseCategories.forEach(category => {
      // find every exercise that references this category ID
      const filteredExercises = exercises.filter(e => {
        return e.exercise_categories ? e.exercise_categories.includes(category.id) : false;
      });

      // create a CategorizedExercises with exercises that reference category
      // only include it IF the category has at least 1 matching exercise
      if (filteredExercises.length > 0) {
        categorizedExercises.push({
          category: category,
          exercises: filteredExercises,
        });
      }
    });

    // Find any exercises with undefined categories or no categories
    const uncategorizedExercises = exercises.filter(e => {
      return e.exercise_categories === undefined || e.exercise_categories.length === 0;
    });

    // if we find any, add them in their own CategorizedExercises
    if (uncategorizedExercises.length > 1) {
      categorizedExercises.push({
        category: { id: undefined, name: 'No Category' },
        exercises: uncategorizedExercises,
      });
    }
    return categorizedExercises;
  }

  openNotes(row: PlannedTierRow) {
    this.dialog
      .open(DocumentTierRowNotesDialogComponent, {
        width: '400px',
        disableClose: true,
        data: {
          name: row.exercise_name,
          notes: row.notes,
          canEdit: this.canEdit,
        },
      })
      .beforeClosed()
      .subscribe(result => {
        row.notes = result || '';
        this.updateTier();
      });
  }

  deleteRow(row: PlannedTierRow) {
    this.tierModel.rows = this.tierModel.rows.filter(r => r.id !== row.id);
    // update order
    this.tierModel.rows.forEach((r, index) => {
      r.order = index + 1;
    });
    this.updateTier();
  }

  filterParameterOverrides(index: number, param: string, value: string) {
    if (isNil(value)) {
      value = '';
    }

    if (
      this.tierTemplate &&
      this.tierTemplate.rows[index] &&
      this.tierTemplate.rows[index].parameter_overrides &&
      this.tierTemplate.rows[index].parameter_overrides[param]
    ) {
      this.parameterOverrides = this.tierTemplate.rows[index].parameter_overrides[param].values
        .map(val => toString(val))
        .filter(val => val.toLowerCase().indexOf(value.toLowerCase()) > -1);
    } else {
      this.parameterOverrides = [];
    }
  }

  getVideo(exerciseId: string) {
    const exercise = this.exercises.find(ex => ex.id === exerciseId);
    return this.resourceService.getResource(exercise, ResourceType.Video);
  }

  hasVideo(exerciseId: string): boolean {
    const video = this.getVideo(exerciseId);
    if (video && video.hls_url) {
      return true;
    }
    return false;
  }

  showVideo(exerciseId: string) {
    const exercise = this.exercises.find(ex => ex.id === exerciseId);
    return this.resourceService.showResource(exercise, ResourceType.Video);
  }

  getExerciseVideoIcon(exerciseId: string) {
    const videoGray = '/assets/images/videoGray.svg';
    const videoGreen = '/assets/images/videoBlue.svg';
    return this.hasVideo(exerciseId) ? videoGreen : videoGray;
  }

  /**
   * Takes a number and returns letter.
   * @example 1 => A, 26 => Z, 27 => AA, 400 => OJ
   * @param order - number to convert
   */
  getRowLetter(order: number): string {
    if (order <= 0) {
      return 'N/A';
    }
    let num = order;
    let str = '';
    const baseChar = 'A'.charCodeAt(0);
    while (num > 0) {
      num -= 1;
      str = String.fromCharCode(baseChar + (num % 26)) + str;
      num = Math.floor(num / 26);
    }
    return str;
  }

  /**
   * Emits addedTierToLibrary event
   */
  addTierToLibrary() {
    this.addedTierToLibrary.emit(this.tier);
  }

  getParameterPlaceholder(param: ParameterOption) {
    let placeholder = this.titlecasePipe.transform(param.param);
    if (param.unit && param.unit !== 'N/A') {
      placeholder += ' (' + param.unit + ')';
    }

    return placeholder;
  }

  openTrainingLoadDialog() {
    this.dialog.open(TrainingLoadNoticeDialogComponent, {
      data: {
        supportsTrainingLoadAnalysis: this.supportsParameterValidation,
      },
    });
  }

  getTooltip(selection: string | number, param: ParameterOption) {
    if (param.type === 'number' && selection && !isNumber(selection)) {
      return `Invalid numeric value: ${selection}`;
    }
  }

  /**
   * Checks for invalid selections assigned to a parameter
   * @returns false if exercise parameter type !== row parameter type, true after all rows are validated
   */
  private checkForParameterValidationSupport() {
    if (
      this.planMetadata &&
      this.planMetadata.migrations &&
      this.planMetadata.migrations.some(migrations => migrations.name === 'std_units_plans')
    ) {
      return true;
    }

    for (let i = 0; i < this.tier.rows.length; i++) {
      const row = this.tier.rows[i];

      if (row.exercise_id) {
        // look up exercise
        const exercise = this.exercises.find(ex => ex.id === row.exercise_id);
        if (exercise) {
          for (const key in row.parameter_selections) {
            // if row parameter selection and exercise parameter exist
            if (row.parameter_selections[key] && exercise.parameters[key]) {
              if (exercise.parameters[key].type === 'int-array') {
                // validate int type
                if (!isNumber(row.parameter_selections[key])) {
                  return false;
                }
              } else if (exercise.parameters[key].type === 'str-array') {
                // validate string type
                if (!isString(row.parameter_selections[key])) {
                  return false;
                }
              }
            }
          }
        }
      }
    }

    // return true if all rows are checked without failing
    return true;
  }

  validateInput($event: any) {
    return $event.keyCode !== 69 && $event.keyCode !== 101 && $event.keyCode !== 189 && $event.keyCode !== 187 && $event.keyCode !== 190;
  }
}
